import type { AdTextHeader, AdTextSbReference } from '@avisoma/shared';
import { Badge } from '../../catalyst/badge';
import { Td, Th } from '../../components/reconstructedDocument/extractionTables/cells';
import {
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from '../../components/reconstructedDocument/extractionTables/tableClasses';

function headerLine(header: AdTextHeader): string {
  return [
    `${header.authority} AD ${header.adNumber}`,
    header.amendment && `Amendment ${header.amendment}`,
    header.effectiveDate && `effective ${header.effectiveDate}`,
    header.replacesAdNumbers?.length && `replaces ${header.replacesAdNumbers.join(', ')}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

function bulletinLabel(reference: AdTextSbReference): string {
  const revision = reference.sbRevision
    ? ` · ${reference.sbRevision}${reference.revisionQualifier === 'or-later' ? ' or later' : ''}`
    : '';
  return `${reference.sbNumber}${revision}`;
}

/**
 * The service bulletins an `Airworthiness Directives Reference` page's text names (DR-5796): the
 * AD identity line from the page header, then one row per bulletin mention with its role, the AD
 * paragraph and the verbatim sentence. Roles all show neutral — nothing here decides which
 * bulletin the AD mandates. Renders nothing when both families are empty.
 */
export function AdTextReferenceCard({
  headers,
  references,
}: {
  headers: AdTextHeader[];
  references: AdTextSbReference[];
}) {
  if (headers.length === 0 && references.length === 0) return null;
  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700">
        <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">AD text: service bulletins named</h2>
      </div>
      {headers.map((header, index) => (
        <p
          key={`${header.adNumber}-${index}`}
          className="px-4 py-3 text-sm text-gray-900 dark:text-zinc-100 border-b border-gray-200 dark:border-zinc-700 last:border-b-0"
        >
          {headerLine(header)}
        </p>
      ))}
      {references.length > 0 && (
        <div className="overflow-x-auto">
          <table className={extractionTableClass}>
            <thead className={extractionTheadClass}>
              <tr>
                <Th>Role</Th>
                <Th>Service bulletin</Th>
                <Th>Paragraph</Th>
                <Th>Quote</Th>
              </tr>
            </thead>
            <tbody className={extractionTbodyClass}>
              {references.map((reference, index) => (
                <tr key={`${reference.sbNumber}-${reference.role}-${reference.paragraph ?? ''}-${index}`}>
                  <Td>
                    <Badge color="zinc">{reference.role.replace(/-/g, ' ')}</Badge>
                  </Td>
                  <Td className="font-mono">{bulletinLabel(reference)}</Td>
                  <Td>{reference.paragraph ?? '—'}</Td>
                  {/* Plain cell: the shared `Td` is nowrap, and the quote must wrap in full. */}
                  <td className="px-2 py-1.5 align-top min-w-[240px] text-xs text-gray-500 dark:text-zinc-400 whitespace-normal">
                    {reference.quote}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
